import {Button, Dialog, Flash, FormControl, IconButton, Stack} from '@primer/react'
import {ArrowLeftIcon, XIcon} from '@primer/octicons-react'
import {useState, useEffect, useCallback, useRef} from 'react'
import type {CopilotSpaceRepositoryResource, GitHubFileFormData, ResourceCounts} from '../types'
import type {CopilotChatRepo} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import type {TreeItem} from '@github-ui/repos-file-tree-view'
import {fetchRepo} from '../utils/fetch-repo'
import type {DirectoryItem} from '@github-ui/code-view-types'
import {useTreeList} from '@github-ui/code-view-shared/hooks/use-tree-list'
import {convertFlatPathsToTreeItems} from '../utils/convert-tree-data'
import {FileTreePicker} from './file-tree-picker/FileTreePicker'
import {RepoSelectPanel} from '@github-ui/copilot-chat/components/RepoSelectPanel'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {FileQueryProvider} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {FilesPageInfoProvider} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {AllShortcutsEnabledProvider} from '@github-ui/code-view-shared/contexts/AllShortcutsEnabledContext'
import {testIdProps} from '@github-ui/test-id-props'
import FileResultsList from './files-search/FileResultsList'

import styles from './MultiFilePicker.module.css'

import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {sendEvent} from '@github-ui/hydro-analytics'

interface Props {
  formData: GitHubFileFormData[]
  onCancel: () => void
  onSaveFiles: (data: GitHubFileFormData[]) => Promise<void> | void
  onSaveRepository: (repo: CopilotSpaceRepositoryResource) => Promise<void> | void
  findFileWorkerPath: string
  attachmentButtonRef: React.RefObject<HTMLButtonElement>
  owner?: string
  resourceCounts?: ResourceCounts
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
  // Whether to hide the back button in the dialog header of the file picker.
  hideBackButton?: boolean
  allowFolderReferences?: boolean
  maxFileCount?: number
}

interface FilePickerFormProps {
  currentRepo: CopilotChatRepo
  localFormData: GitHubFileFormData[]
  setLocalFormData: (data: GitHubFileFormData[]) => void
  fetchError: string | null
  onFileCountChange?: (count: number) => void
  expandedPath: string
  resourceCounts?: ResourceCounts
  allowFolderReferences?: boolean
  maxFileCount?: number
  onRejectionMessage?: (message: string | null) => void
  onDisplayCountChange?: (count: number) => void
}

function createFormDataEntry(
  path: string,
  type: 'github_file' | 'github_folder',
  currentRepo: CopilotChatRepo,
): GitHubFileFormData {
  return {
    id: `${currentRepo.id}${type === 'github_folder' ? '-folder' : ''}-${path}`,
    repositoryId: currentRepo.id,
    nwo: `${currentRepo.ownerLogin}/${currentRepo.name}`,
    filePath: path,
    type,
  }
}

function createRepositoryResource(repo: CopilotChatRepo): CopilotSpaceRepositoryResource {
  return {
    type: 'repository',
    id: `${repo.id}`,
    repositoryId: repo.id,
    nwo: `${repo.ownerLogin}/${repo.name}`,
    ownerAvatarUrl: repo.ownerAvatarUrl,
    ownerType: repo.ownerType,
    markedForDestroy: false,
  }
}

function processFileSelection(
  selectedItems: Set<string>,
  allDirectories: Set<string>,
  currentRepo: CopilotChatRepo,
  shouldAutoFolderize: boolean,
  effectiveMaxFileCount: number,
  allowFolderReferences: boolean,
): GitHubFileFormData[] {
  const selectedFolders = Array.from(selectedItems).filter(path => allDirectories.has(path))
  const selectedFiles = Array.from(selectedItems).filter(path => !allDirectories.has(path))

  if (!allowFolderReferences) {
    return selectedFiles.map(path => createFormDataEntry(path, 'github_file', currentRepo))
  }

  const formData: GitHubFileFormData[] = []
  const processedPaths = new Set<string>()

  for (const folder of selectedFolders) {
    if (selectedFolders.some(other => other !== folder && folder.startsWith(`${other}/`))) {
      continue
    }

    const filesInFolder = selectedFiles.filter(file => file.startsWith(`${folder}/`))

    if (allowFolderReferences && filesInFolder.length === 0) {
      formData.push(createFormDataEntry(folder, 'github_folder', currentRepo))
      continue
    }
    if (allowFolderReferences && filesInFolder.length >= effectiveMaxFileCount) {
      formData.push(createFormDataEntry(folder, 'github_folder', currentRepo))
      for (const file of filesInFolder) {
        processedPaths.add(file)
      }
    } else {
      for (const file of filesInFolder) {
        formData.push(createFormDataEntry(file, 'github_file', currentRepo))
        processedPaths.add(file)
      }
    }
  }

  const uncoveredFiles = selectedFiles.filter(file => !processedPaths.has(file))

  if (!shouldAutoFolderize || !allowFolderReferences) {
    for (const file of uncoveredFiles) {
      formData.push(createFormDataEntry(file, 'github_file', currentRepo))
    }
  } else {
    const filesByDirectory = new Map<string, string[]>()

    for (const file of uncoveredFiles) {
      const lastSlash = file.lastIndexOf('/')
      const parentDir = lastSlash > 0 ? file.substring(0, lastSlash) : ''

      if (!filesByDirectory.has(parentDir)) {
        filesByDirectory.set(parentDir, [])
      }
      filesByDirectory.get(parentDir)?.push(file)
    }

    for (const [parentDir, files] of filesByDirectory) {
      if (parentDir === '') {
        for (const file of files) {
          formData.push(createFormDataEntry(file, 'github_file', currentRepo))
        }
      } else {
        formData.push(createFormDataEntry(parentDir, 'github_folder', currentRepo))
      }
    }
  }

  return formData
}

function truncatePathsByLength(path: string, maxLength = 30): string {
  const truncated = path.length <= maxLength ? path : `...${path.slice(-maxLength)}`
  // eslint-disable-next-line github/unescaped-html-literal
  return `<b>${truncated}</b>`
}

function formatUnsupportedFilesError(files: string[]): string {
  if (files.length === 1) {
    return `This file can't be submitted: ${files[0]}. Only supported file types are allowed.`
  }

  const displayFiles = files.slice(0, 3).map(path => truncatePathsByLength(path))
  const remainingCount = files.length - 3

  let message = `One or more files can't be submitted: ${displayFiles.join(', ')}`
  if (remainingCount > 0) {
    message += `, and ${remainingCount} more`
  }
  return `${message}. Only supported file types are allowed.`
}

function FilePickerForm({
  currentRepo,
  setLocalFormData,
  fetchError,
  onFileCountChange,
  expandedPath,
  resourceCounts,
  allowFolderReferences,
  maxFileCount,
  onRejectionMessage,
  onDisplayCountChange,
}: FilePickerFormProps) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set())
  const [allDirectories, setAllDirectories] = useState<Set<string>>(() => new Set())

  const effectiveMaxFileCount = maxFileCount ?? 50

  // call useTreeList
  const {list, directories, loading} = useTreeList(currentRepo.commitOID, true)

  const [rootItems, setRootItems] = useState<Array<TreeItem<DirectoryItem>>>([])

  // Update tree items when list or directories change
  useEffect(() => {
    const response = {paths: list, directories}
    const items = convertFlatPathsToTreeItems(response)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRootItems(items)
    setAllDirectories(new Set(directories))
  }, [list, directories, setRootItems, currentRepo.commitOID])

  // Track selected files and update form data
  useEffect(() => {
    const selectedFiles = Array.from(selectedItems).filter(path => !allDirectories.has(path))
    const selectedDirectories = Array.from(selectedItems).filter(path => allDirectories.has(path))

    onFileCountChange?.(selectedFiles.length)

    let shouldAutoFolderize = false

    if (allowFolderReferences && selectedFiles.length > 0) {
      const selectedFolderPrefixes = selectedDirectories.map(f => `${f}/`)
      const uncoveredFiles = selectedFiles.filter(
        path => !selectedFolderPrefixes.some(prefix => path.startsWith(prefix)),
      )

      const hasGroupableFiles = uncoveredFiles.some(path => path.includes('/'))
      shouldAutoFolderize = uncoveredFiles.length > effectiveMaxFileCount && hasGroupableFiles
    }

    const result = processFileSelection(
      selectedItems,
      allDirectories,
      currentRepo,
      shouldAutoFolderize,
      effectiveMaxFileCount,
      allowFolderReferences ?? false,
    )
    setLocalFormData(result)

    if (onDisplayCountChange) {
      onDisplayCountChange(result.length)
    }
  }, [
    selectedItems,
    allDirectories,
    currentRepo,
    setLocalFormData,
    onFileCountChange,
    onDisplayCountChange,
    allowFolderReferences,
    effectiveMaxFileCount,
  ])

  const getItemUrl = useCallback(
    (item: DirectoryItem) =>
      `/${`${currentRepo.name}/${currentRepo.ownerLogin}`}/tree/${currentRepo.refInfo.name}/${item.path}`,
    [currentRepo],
  )

  const handleSelectionChange = useCallback(
    (newSelectedItems: Set<string>) => {
      if (newSelectedItems.size <= selectedItems.size) {
        setSelectedItems(newSelectedItems)
        onRejectionMessage?.(null)
        return
      }

      if (!allowFolderReferences) {
        const selectedFiles = Array.from(newSelectedItems).filter(path => !allDirectories.has(path))

        const capacity = resourceCounts
          ? resourceCounts.maxResourceCount - resourceCounts.currentResourceCount
          : effectiveMaxFileCount

        if (selectedFiles.length > capacity) {
          const errorMessage = resourceCounts
            ? `${resourceCounts.currentResourceCount + selectedFiles.length} / ${
                resourceCounts.maxResourceCount
              } possible sources selected, add fewer files.`
            : `Up to ${effectiveMaxFileCount} files can be added at a time.`

          onRejectionMessage?.(errorMessage)
          setSelectedItems(newSelectedItems)
          return
        }
        onRejectionMessage?.(null)
        setSelectedItems(newSelectedItems)
        return
      }

      const selectedFiles = Array.from(newSelectedItems).filter(path => !allDirectories.has(path))
      const selectedFolderPrefixes = Array.from(newSelectedItems)
        .filter(path => allDirectories.has(path))
        .map(f => `${f}/`)

      const uncoveredFiles = selectedFiles.filter(
        path => !selectedFolderPrefixes.some(prefix => path.startsWith(prefix)),
      )

      const hasGroupableFiles = uncoveredFiles.some(path => path.includes('/'))
      const shouldAutoFolderize = uncoveredFiles.length > effectiveMaxFileCount && hasGroupableFiles

      const resultingFormData = processFileSelection(
        newSelectedItems,
        allDirectories,
        currentRepo,
        shouldAutoFolderize,
        effectiveMaxFileCount,
        allowFolderReferences,
      )

      const attachmentCount = resultingFormData.length

      if (attachmentCount > effectiveMaxFileCount) {
        setSelectedItems(newSelectedItems)
        return
      }

      setSelectedItems(newSelectedItems)
    },
    [
      selectedItems,
      effectiveMaxFileCount,
      allDirectories,
      allowFolderReferences,
      onRejectionMessage,
      currentRepo,
      resourceCounts,
    ],
  )

  return (
    <div className={styles.Box}>
      <FileTreePicker
        rootItems={rootItems}
        expandedPath={expandedPath}
        selectedItemRef={() => {}}
        setRootItems={setRootItems}
        navigateOnClick={false}
        directoryNavigateOnClick={false}
        selectedItems={selectedItems}
        onSelectionChange={handleSelectionChange}
        loading={loading || false}
        fetchError={!!fetchError}
        getItemUrl={getItemUrl}
        allowFolderReferences={Boolean(allowFolderReferences)}
        sortDirectoryItems={items => items.sort((a, b) => a.data.name.localeCompare(b.data.name))}
      />
    </div>
  )
}

function CloseButton({onClose}: {onClose: () => void}) {
  return <IconButton icon={XIcon} aria-label="Close" onClick={onClose} variant="invisible" />
}

export function MultiFilePicker({
  formData,
  onCancel,
  onSaveFiles,
  onSaveRepository,
  findFileWorkerPath,
  attachmentButtonRef,
  owner,
  resourceCounts,
  initialRepo,
  onRepoSelect,
  hideBackButton = false,
  allowFolderReferences = false,
  maxFileCount = 50,
}: Props) {
  const [repoDetailsCache, setRepoDetailsCache] = useState<Record<number, CopilotChatRepo>>({})
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [currentRepo, setCurrentRepo] = useState<CopilotChatRepo | null>(initialRepo || null)
  const [openRepoPicker, setOpenRepoPicker] = useState(true)
  const [localFormData, setLocalFormData] = useState<GitHubFileFormData[]>(formData)
  const [isSaving, setIsSaving] = useState(false)
  const [savingError, setSavingError] = useState<string | null>(null)
  const [expandedPath, setExpandedPath] = useState('')
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null)
  const [displayCount, setDisplayCount] = useState(0)
  const [selectedFileCount, setSelectedFileCount] = useState(0)

  const selectingRepo = useRef(false)

  const fetchRepoAndUpdateState = async (repoID: number): Promise<void> => {
    selectingRepo.current = true

    let payload: CopilotChatRepo

    if (repoDetailsCache[repoID]) {
      payload = repoDetailsCache[repoID]
      setCurrentRepo(payload)
      onRepoSelect?.(payload)
      return
    }

    try {
      payload = await fetchRepo(repoID)

      setRepoDetailsCache(prev => ({
        ...prev,
        [repoID]: payload,
      }))

      setCurrentRepo(payload)
      onRepoSelect?.(payload)
      setOpenRepoPicker(false)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
      setFetchError(errorMessage)
    }
    selectingRepo.current = false
  }

  const onSelectRepo = () => {
    onRepoSelect?.(null)
    setCurrentRepo(null)
    setLocalFormData([])
    setOpenRepoPicker(true)
  }

  const validateTotalResourceCount = resourceCounts !== undefined
  const countToValidate = validateTotalResourceCount ? selectedFileCount : displayCount
  const capacity = validateTotalResourceCount
    ? resourceCounts.maxResourceCount - resourceCounts.currentResourceCount
    : maxFileCount

  const fileCountExceeded = countToValidate > capacity

  const handleSave = async () => {
    setIsSaving(true)

    try {
      if (countToValidate === 0 && currentRepo) {
        sendEvent('copilot-spaces.click', {target: 'SAVED_REPOSITORY_FROM_FILE_PICKER'})
        await onSaveRepository(createRepositoryResource(currentRepo))
        return
      }

      if (fileCountExceeded) {
        return
      }

      sendEvent('copilot-spaces.click', {target: 'SAVED_FILES_FROM_FILE_PICKER'})
      await onSaveFiles(localFormData)
    } catch (error: unknown) {
      let errorMessage = 'An unknown error occurred'

      if (error && typeof error === 'object' && 'base' in error && typeof error.base === 'string') {
        if (error.base.startsWith('unsupported_file_type') && 'files' in error) {
          errorMessage = formatUnsupportedFilesError(error.files as string[])
        } else if (error.base.startsWith('The size of the space exceeds the current limit')) {
          errorMessage = 'The size of the space exceeds the current limit'
        } else {
          errorMessage = error.base
        }
      }

      setSavingError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    setSavingError(null)
  }, [localFormData])

  const errorMessage = fileCountExceeded
    ? validateTotalResourceCount
      ? `${resourceCounts.currentResourceCount + selectedFileCount} / ${
          resourceCounts.maxResourceCount
        } possible sources selected, add fewer files.`
      : `Up to ${maxFileCount} items can be added at a time.`
    : rejectionMessage

  const hasError = fileCountExceeded || Boolean(rejectionMessage)
  const addButtonText = countToValidate === 0 ? 'repository' : ''

  return (
    <>
      <RepoSelectPanel
        selectionVariant="instant"
        open={!currentRepo && openRepoPicker}
        onOpenChange={newOpen => {
          // Ignore while loading repo data in order to avoid flash
          if (!selectingRepo.current) {
            setOpenRepoPicker(newOpen)
            onCancel()
          }
        }}
        selectedRepoIds={new Set()}
        onSelectRepo={async (repoId: number) => {
          if (!repoId) return
          setLocalFormData([])
          sendEvent('copilot-spaces.click', {target: 'SELECTED_REPO_FROM_REPO_PICKER'})
          await fetchRepoAndUpdateState(repoId)
        }}
        cancelReturnFocusRef={attachmentButtonRef}
        submitReturnFocusRef={attachmentButtonRef}
        error={fetchError ?? undefined}
        ownerDisplayLogin={owner}
        includeForks
      />

      {currentRepo && (
        <Dialog
          onClose={onCancel}
          title={`Add files from ${currentRepo.ownerLogin}/${currentRepo.name}`}
          width="large"
          position={{narrow: 'fullscreen'}}
          renderHeader={({onClose}) => {
            return (
              <Dialog.Header>
                <div className={styles.dialogHeader}>
                  <Dialog.Title>
                    <Stack align="center" direction="horizontal" gap="none">
                      {!hideBackButton && (
                        <Stack.Item>
                          <IconButton
                            icon={ArrowLeftIcon}
                            aria-label="Back"
                            onClick={onSelectRepo}
                            variant="invisible"
                          />
                        </Stack.Item>
                      )}
                      <Stack.Item grow className="mx-1">
                        Select folders and files in {currentRepo.ownerLogin}/{currentRepo.name}
                      </Stack.Item>
                      <Stack.Item>
                        <CloseButton onClose={() => onClose('close-button')} />
                      </Stack.Item>
                    </Stack>
                  </Dialog.Title>
                </div>
                <Dialog.Subtitle className="mt-0">
                  <CurrentRepositoryProvider repository={currentRepo as unknown as Repository}>
                    <FileQueryProvider>
                      <FilesPageInfoProvider
                        refInfo={{
                          name: currentRepo.refInfo.name,
                          canEdit: true,
                          listCacheKey: 'anything',
                          currentOid: currentRepo.commitOID,
                        }}
                        path="test"
                        action="tree"
                        copilotAccessAllowed
                      >
                        <AllShortcutsEnabledProvider allShortcutsEnabled={false}>
                          <FileResultsList
                            commitOid={currentRepo.commitOID}
                            findFileWorkerPath={findFileWorkerPath}
                            onItemSelected={(path?: string) => {
                              if (path) {
                                setExpandedPath(path)
                              }
                            }}
                            config={{
                              enableOverlay: true,
                              disableNavigation: true,
                              searchPlaceholder: 'Search for files or folders',
                              actionText: 'Search for files or folders',
                            }}
                          />
                        </AllShortcutsEnabledProvider>
                      </FilesPageInfoProvider>
                    </FileQueryProvider>
                  </CurrentRepositoryProvider>
                </Dialog.Subtitle>
              </Dialog.Header>
            )
          }}
          renderFooter={() => (
            <Dialog.Footer>
              <div className="d-flex flex-justify-between flex-items-center width-full">
                <div className="d-flex flex-items-center">
                  {errorMessage && <FormControl.Validation variant="error">{errorMessage}</FormControl.Validation>}
                </div>
                <div className="d-flex flex-row flex-justify-end gap-2">
                  <Button onClick={onCancel}>Cancel</Button>
                  <Button
                    {...testIdProps('save-resource-button')}
                    variant="primary"
                    onClick={handleSave}
                    count={
                      isSaving
                        ? undefined
                        : (validateTotalResourceCount ? selectedFileCount : displayCount) || undefined
                    }
                    loading={isSaving}
                    disabled={hasError}
                  >
                    Add <span>{addButtonText}</span>
                  </Button>
                </div>
              </div>
            </Dialog.Footer>
          )}
          renderBody={() => (
            <div className="d-flex flex-column">
              {fetchError && <span>{fetchError}</span>}
              {savingError && (
                <div className="position-sticky top-0 bgColor-default flash-banner">
                  <Flash full variant="danger">
                    <VerifiedHTMLText as="span" html={savingError as SafeHTMLString} />
                  </Flash>
                </div>
              )}
              <CurrentRepositoryProvider repository={currentRepo as unknown as Repository}>
                <FileQueryProvider>
                  <FilesPageInfoProvider
                    refInfo={{
                      name: currentRepo.refInfo.name,
                      canEdit: true,
                      listCacheKey: 'anything',
                      currentOid: currentRepo.commitOID,
                    }}
                    path="test"
                    action="tree"
                    copilotAccessAllowed
                  >
                    <FilePickerForm
                      expandedPath={expandedPath}
                      currentRepo={currentRepo}
                      localFormData={localFormData}
                      setLocalFormData={setLocalFormData}
                      onFileCountChange={setSelectedFileCount}
                      onDisplayCountChange={setDisplayCount}
                      fetchError={fetchError}
                      resourceCounts={resourceCounts}
                      allowFolderReferences={allowFolderReferences}
                      maxFileCount={maxFileCount}
                      onRejectionMessage={setRejectionMessage}
                    />
                  </FilesPageInfoProvider>
                </FileQueryProvider>
              </CurrentRepositoryProvider>
            </div>
          )}
          className={styles.Dialog}
        />
      )}
    </>
  )
}

try{ FilePickerForm.displayName ||= 'FilePickerForm' } catch {}
try{ CloseButton.displayName ||= 'CloseButton' } catch {}
try{ MultiFilePicker.displayName ||= 'MultiFilePicker' } catch {}